/**
 * @file: MeHomeScreen.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Root of the Me tab: who the member is, shortcuts to their own records, the
 * rewards balance and their recent work. Editing the profile itself lives one
 * level down on `_profile`.
 */

import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import type { MeNavParams } from '@euler/app/flows/me';
import { AchievementsCard } from '@euler/app/flows/me/components/AchievementsCard';
import {
  MeHeaderRight,
  MeHeaderSettingsButton,
  ProfileCard,
  ProfileHero,
  ProfileSummaryProps,
} from '@euler/app/flows/me/components/ProfileHeader';
import {
  RewardsCard,
  RewardsLink,
} from '@euler/app/flows/me/components/RewardsCard';
import { WeeklyActivityCard } from '@euler/app/flows/me/components/WeeklyActivityCard';
import { MK, kHeroOverlap } from '@euler/app/flows/me/components/meKit';
import { useMyPerformance } from '@euler/app/flows/me/functions/useMyPerformance';
import { useMemberAccount } from '@euler/app/flows/settings/functions/useMemberAccount';
import { useWithdraw } from '@euler/app/flows/settings/functions/useWithdraw';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { roleStringToUserRoles } from '@euler/functions/orgUserUtils';
import { useHasAnyProductAuthorizationContract } from '@euler/functions/product/useHasAnyProductAuthorizationContract';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { roleNameFromRoleType } from '@euler/functions/user/util';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { OrgUserRoleType, StoreFeature } from '@euler/model/enum';
import { formatMoney } from '@euler/utils/formatMoney';
import { usePersistFn, useWatchValue } from '@euler/utils/hooks';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useLayoutEffect, useMemo } from 'react';
import { RefreshControl, ScrollView, View } from 'react-native';

export const MeHomeScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    const isFocused = useIsFocused();
    const contentContainerStyle = useScreenContentContainerStyle();
    const navigation = useNavigation<StackNavigationProp<MeNavParams>>();
    const { user } = useCurrentUserDangerously();
    const role = useCurrentUserRole();
    // Rewards come from product rewards, a feature of its own.
    const hasAnyContract = useHasAnyProductAuthorizationContract();
    const productReward = useStoreFeature(StoreFeature.ProductReward);
    const hasProductAuthContract = hasAnyContract && productReward.enabled;
    const { state: account, invalidate, refreshing } = useMemberAccount();
    const { onWithdraw } = useWithdraw();
    const { state: performance, reload: reloadPerformance } =
      useMyPerformance();

    useStatusBarStyle('light', !isDesktop && isFocused);

    useWatchValue(isFocused, (prev, curr) => {
      if (prev != null && !prev && curr) {
        void invalidate(true);
        reloadPerformance();
      }
    });

    const roleHelper = new OrgUserRoleHelper(role);
    const isServiceAgent = roleHelper.hasRole(OrgUserRoleType.ServiceAgents);
    const roles = useMemo(
      () =>
        roleStringToUserRoles(role)
          // An access level rather than a job, so it isn't worn as a tag.
          .filter(r => r !== OrgUserRoleType.Administrator)
          .map(roleNameFromRoleType),
      [role],
    );

    const onProfilePress = usePersistFn(() => {
      navigation.navigate('_profile', {});
    });

    const onSettingsPress = usePersistFn(() => {
      navigation.navigate('settings', {});
    });

    const onHelpPress = usePersistFn(() => {
      navigation.navigate('help', {});
    });

    const rewardsLinks = useMemo<RewardsLink[]>(() => {
      const links: RewardsLink[] = [
        {
          key: 'rewards',
          label: '奖励记录',
          icon: 'gift',
          onPress: () => navigation.navigate('_productReferralHistory', {}),
        },
      ];
      if (isServiceAgent) {
        links.push({
          key: 'sharing',
          label: '分成记录',
          icon: 'share',
          onPress: () =>
            navigation.navigate('_productReferralHistory', {
              isServiceAgentShare: true,
            }),
        });
      }
      links.push({
        key: 'transactions',
        label: '交易明细',
        icon: 'bill-list',
        onPress: () => navigation.navigate('_transactions', {}),
      });
      return links;
    }, [isServiceAgent, navigation]);

    const balance =
      hasProductAuthContract && account != null
        ? formatMoney(account.balance, { prefix: true })
        : undefined;
    const canWithdraw = account != null && account.balance > 0;

    useLayoutEffect(() => {
      navigation.setOptions({
        headerLeft: () => <MeHeaderSettingsButton onPress={onSettingsPress} />,
        headerRight: () => (
          <MeHeaderRight
            balance={balance}
            onBalancePress={canWithdraw ? onWithdraw : undefined}
            onHelpPress={onHelpPress}
            onSettingsPress={onSettingsPress}
          />
        ),
      });
    }, [
      balance,
      canWithdraw,
      navigation,
      onHelpPress,
      onSettingsPress,
      onWithdraw,
    ]);

    const profileProps: ProfileSummaryProps = {
      name: user.name ?? user.nick ?? user.userName ?? '',
      avatar: user.avatar,
      mobile: user.mobile,
      jobTitle: user.jobTitle,
      roles,
      onPress: onProfilePress,
      onHelpPress,
      contentStyle: contentContainerStyle,
    };

    const gap = rpx(16);

    return (
      <ScrollView
        style={{ flex: 1, backgroundColor: MK.canvas }}
        contentContainerStyle={{ paddingBottom: rpx(24) }}
        refreshControl={
          <RefreshControl
            refreshing={!!refreshing}
            onRefresh={() => {
              void invalidate();
              reloadPerformance();
            }}
            tintColor={isDesktop ? undefined : MK.onBrand}
          />
        }
      >
        {!isDesktop && <ProfileHero {...profileProps} />}
        <View
          style={[
            {
              paddingHorizontal: rpx(16),
              gap,
              // On phones the first card rides up over the hero's tail.
              ...(isDesktop
                ? { paddingTop: gap }
                : { marginTop: -rpx(kHeroOverlap) }),
            },
            contentContainerStyle,
          ]}
        >
          {isDesktop && <ProfileCard {...profileProps} />}
          <AchievementsCard state={performance} onRetry={reloadPerformance} />
          {/* The weekly chart tracks orders, which only the store-facing
              counts carry. */}
          {performance.kind === 'ready' &&
            performance.performance.serviceAgent != null && (
              <WeeklyActivityCard
                days={performance.performance.serviceAgent.daily}
              />
            )}
          {hasProductAuthContract && (
            <RewardsCard
              account={account}
              links={rewardsLinks}
              onWithdraw={onWithdraw}
            />
          )}
        </View>
      </ScrollView>
    );
  },
  {
    title: '我的',
  },
);
